/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { ISwatchItem, ProductComponentSwatchComponent, RatingComponent, AddToWishlistComponent } from '@msdyn365-commerce/components';
import { PriceComponent, IPriceComponentResources } from './price.component';
import {
    getCatalogId,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    CommerceList,
    ProductDimension,
    ProductPrice,
    ProductSearchResult
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
import { ProductSearchResultExpanded } from '../classes/ProductSearchResultExpanded';
import { isValidImage } from '../functions/is-valid-image';
import Show from '../../components/common/Show/show';
import { generateImagePath } from '../functions/image-path';
import ProductLabel from './product-label.component';

export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResultExpanded }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    wishlist?: CommerceList[];
    wishlistInProductCard?: boolean;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }
    // bs extension - place media library images first
    const isMediaLibraryImage = (item: string) => {
        return item && item.startsWith('Products');
    };

    if (coreContext.request.app.config.filterLegacyImageUrls) {
        const imageUrl = swatchProductImageUrls
            .sort((a, b) => Number(isMediaLibraryImage(b)) - Number(isMediaLibraryImage(a)))
            .find(item => {
                return isValidImage(item);
            });
        if (imageUrl) {
            return generateImageUrl(imageUrl, coreContext.request.apiSettings);
        }
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

function getProductImageFromMediaLibrary(context: ICoreContext, product?: ProductSearchResult): string {
    const defaultSwatch = getDefaultSwatchSelected(context, product);
    return generateImageUrl(generateImagePath(product?.ItemId ?? '', defaultSwatch?.SwatchValue ?? ''), context.request.apiSettings) ?? '';
}
/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
    if (!unitOfMeasure) {
        return null;
    }
    return (
        <div className='msc-product__unit-of-measure'>
            <span>{unitOfMeasure}</span>
        </div>
    );
}

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
    if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
        return null;
    }

    return (
        <div className='msc-product__availability'>
            <span>{inventoryAvailabilityLabel}</span>
        </div>
    );
}

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: ProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel,
    wishlist,
    wishlistInProductCard
}) => {
    const product = data.product;

    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }

    //BS CUSTOM - generate media library url
    var productImage: string;
    if (context.app.config.defaultToMediaLibraryImages) {
        productImage = getProductImageFromMediaLibrary(context, product) ?? '';
    } else {
        productImage = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl ?? '';
    }
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImage);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImage);
    }, [productUrl, productPageUrlFromSwatch, productImage]);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    if (!product) {
        return null;
    }
    const productLabels = <ProductLabel product={product} price={product.ProductPrice} context={context} />;

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    //BS CUSTOM - set onSelectDimension to never update
                    return (
                        <React.Fragment key={item.RecordId}>
                            <ProductComponentSwatchComponent context={context} swatches={swatches} onSelectDimension={() => {}} />
                            <Show condition={product?.additionalSwatches !== undefined}>
                                <p className='Custom_additionalSwatces'>+{product?.additionalSwatches}</p>
                            </Show>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';

    return (
        <>
            {isEnabledProductDescription ? (
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        context.cultureFormatter.formatCurrency(product.Price),
                        product.AverageRating,
                        ratingAriaLabel,
                        product.TotalRatings,
                        ratingCountAriaLabel
                    )}
                    className='msc-product'
                    {...attribute}
                >
                    {productLabels}
                    <div className='msc-product__image__description'>
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                        <div className='msc-product__title_description'>
                            <h5 className='msc-product__title__text'>{product.Name}</h5>
                            {renderPrice(
                                context,
                                typeName,
                                id,
                                product.BasePrice,
                                product.Price,
                                savingsText,
                                freePriceText,
                                originalPriceText,
                                currentPriceText,
                                isPriceMinMaxEnabled,
                                priceResources,
                                product.ProductPrice
                            )}
                            {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                            {renderDescription(product.Description)}
                        </div>
                        {wishlistInProductCard && (
                            <div className='wishlist-btn'>
                                <AddToWishlistComponent
                                    className=''
                                    nameOfWishlist=''
                                    addToWishlistButtonText=''
                                    removeFromWishlistButtonText=''
                                    context={context}
                                    id={id}
                                    data={{
                                        product: {
                                            BasePrice: Number(product.BasePrice),
                                            ProductTypeValue: Number(id),
                                            AdjustedPrice: product.Price,
                                            RecordId: product.RecordId,
                                            Price: product.Price
                                        },
                                        wishlists: wishlist
                                    }}
                                    typeName={typeName}
                                />
                            </div>
                        )}
                    </div>
                </a>
            ) : (
                <div>
                    <a
                        href={productPageUrl}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={renderLabel(
                            product.Name,
                            context.cultureFormatter.formatCurrency(product.Price),
                            product.AverageRating,
                            ratingAriaLabel
                        )}
                        className='msc-product'
                        {...attribute}
                    >
                        {productLabels}
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                        <div className='msc-product__details'>
                            <h5 className='msc-product__title'>{product.Name}</h5>
                            {renderPrice(
                                context,
                                typeName,
                                id,
                                product.BasePrice,
                                product.Price,
                                savingsText,
                                freePriceText,
                                originalPriceText,
                                currentPriceText,
                                undefined,
                                priceResources,
                                product.ProductPrice
                            )}
                            {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                        </div>
                    </a>
                    {wishlistInProductCard && (
                        <div className='wishlist-btn'>
                            <AddToWishlistComponent
                                className=''
                                nameOfWishlist=''
                                addToWishlistButtonText=''
                                removeFromWishlistButtonText=''
                                context={context}
                                id={id}
                                data={{
                                    product: {
                                        BasePrice: Number(product.BasePrice),
                                        ProductTypeValue: Number(id),
                                        AdjustedPrice: product.Price,
                                        RecordId: product.RecordId,
                                        Price: product.Price
                                    },
                                    wishlists: wishlist
                                }}
                                typeName={typeName}
                            />
                        </div>
                    )}
                </div>
            )}
            {renderProductDimensions(product.AttributeValues)}
            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
            {renderProductAvailability(inventoryLabel)}
            {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
            {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
        </>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    basePrice?: number,
    adjustedPrice?: number,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    productPrice?: ProductPrice
): JSX.Element | null {
    const price: ProductPrice =
        productPrice !== undefined
            ? productPrice
            : {
                  BasePrice: basePrice,
                  AdjustedPrice: adjustedPrice,
                  CustomerContextualPrice: adjustedPrice
              };
    return (
        <PriceComponent
            context={context}
            id={id}
            typeName={typeName}
            data={{ price }}
            savingsText={savingsText}
            freePriceText={freePriceText}
            originalPriceText={originalPriceText}
            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
            priceResources={priceResources}
            displayLabelOnPrice={false}
        />
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
